import angular from 'angular';
import moment from 'moment';

import { find, some, map, reduce, forEach, filter, uniqBy } from 'lodash';

import analytics from 'common/analytics';
import {
    PERMISSION_TEAM_EDIT,
    PERMISSION_TEAM_DELETE,
    PERMISSION_TEAM_ADD_CALENDAR,
    PERMISSION_TEAM_ADD_MEMBER,
    PERMISSION_TEAM_MANAGE_EVENTS,
    PERMISSION_TEAM_MANAGE_CATEGORIES,
    PERMISSION_CALENDAR_EDIT,
    PERMISSION_CALENDAR_DELETE,
    PERMISSION_CALENDAR_MANAGE_EVENTS,
    hasPermission
} from 'application/util';
import { getErrorMessage } from 'application/util/error.util';
import { every } from 'application/util/time.util';
import { getMaxNumberOfCollaborators } from 'common/util/plan.util';
import { transactionHasTerms } from 'application/util/transaction.util';

import './calendar.style.sass';
import template from './calendar.tpl.pug';

const TRANSACTIONS_BY_EMAIL_NOTIFICATION_KEY = 'com.halcyonmobile.dollarbird3.userdefkey.showTransactionsByEmailNotification.v3';

class CalendarController {
    /* @ngInject */
    constructor($q, $state, $scope, $element, eventUtil, timeUtil, sessionService, balanceService, eventService, preferenceService, suggestionService, autoCategoryService, calendarService, categoryService, teamService, modalService, storageService, notificationService) {
        this.$q = $q;
        this.$state = $state;
        this.$scope = $scope;
        this.$element = $element;
        this.timeUtil = timeUtil;
        this.eventUtil = eventUtil;
        this.sessionService = sessionService;
        this.balanceService = balanceService;
        this.eventService = eventService;
        this.preferenceService = preferenceService;
        this.suggestionService = suggestionService;
        this.autoCategoryService = autoCategoryService;
        this.calendarService = calendarService;
        this.categoryService = categoryService;
        this.teamService = teamService;
        this.modalService = modalService;
        this.storageService = storageService;
        this.notificationService = notificationService;

        this.updateEventLimit = this.updateEventLimit.bind(this);
    }

    $onInit() {
        this.loadingTeams = 0;
        this.loadingMonthlyBalances = 0;
        this.loadingDailyBalances = 0;

        this.loading = true;
        this.calendarHighlightPreference = 'selected';
        this.calendarHighlightPalette = null;
        this.useCategoryIcons = false;
        this.balanceWarningThreshold = null;

        this.categoryService.onUpdate(this.refreshVisibleCategoryMetadata.bind(this));
        this.initializeTransactionsByEmailNotification();
        this.$scope.$on('categoryIconStyleUpdated', (event, style) => {
            this.useCategoryIcons = style === 'icons';
        });
        this.$scope.$on('balanceWarningThresholdUpdated', () => {
            this.balanceWarningThreshold = this.getBalanceWarningThreshold({});
        });

        this.$q.all([
            this.preferenceService.get(),
            this.loadTeams(this.sessionService.initialize())
        ]).then(([preferences, teams]) => {
            this.startOfWeek = preferences.startWeekOnSunday ? 0 : 1;
            this.autoCategorization = !preferences.autoCategorizationOff;
            this.calendarHighlightPreference = preferences.calendarHighlightColor || 'selected';
            this.balanceWarningThreshold = this.getBalanceWarningThreshold(preferences);
            const categoryIconStyle = preferences.categoryIconStyle || this.storageService.getUserItem('category-icon-style') || 'icons';
            this.useCategoryIcons = categoryIconStyle === 'icons';

            this.timestamp = moment().startOf('month').valueOf();

            this.weekdays = this.createWeekdays();
            this.months = this.createMonths(this.timestamp);
            this.days = this.createDays(this.timestamp);

            this.handleDefaultCalendarNotification(this.plan || {}, teams);

            this.multiple = this.storageService.getUserItem('multiple-calendars');

            return this.selectTeamIdAndCalendarIds(
                +this.teamId,
                map(this.calendarIds, (calendarId) => +calendarId),
                this.storageService.getUserItem('selected-team'),
                this.storageService.getUserItem('selected-calendars')
            );
        }).catch(() => null).finally(() => {
            this.dailyRefreshId = every('day', () => this.refreshMonthlyBalances(this.timestamp).catch(() => null));
            this.pollRefreshId = every('minute', this.reloadCalendar.bind(this));

            this.loading = false;
        });
    }

    $postLink() {
        this.calendarRoot = this.$element && this.$element[0] ? this.$element[0].querySelector('#calendar-page') : null;
        this.applyCalendarHighlight();
    }

    reloadCalendar() {
        return this.getSelectedTeamAndCalendars().then(({ selectedCalendarIds }) => this.refreshCalendar(selectedCalendarIds)).catch(() => null);
    }

    getBalanceWarningThreshold(preferences) {
        const storedEnabled = this.storageService.getUserItem('balance-warning-threshold-enabled');
        const hasStoredEnabled = storedEnabled != null;
        const effectiveEnabled = hasStoredEnabled
            ? storedEnabled === true || storedEnabled === 'true'
            : !(preferences && preferences.balanceWarningThresholdEnabled === false);

        if (!effectiveEnabled) {
            return null;
        }

        const storedThreshold = this.storageService.getUserItem('balance-warning-threshold');
        const hasStoredThreshold = storedThreshold != null && storedThreshold !== '';
        const threshold = hasStoredThreshold
            ? storedThreshold
            : preferences && (
                preferences.balanceWarningThreshold
                || preferences.calendarBalanceWarningThreshold
                || preferences.balanceThreshold
                || preferences.calendarBalanceThreshold
                || preferences.warningBalanceThreshold
            );

        return Number.isFinite(+threshold) && +threshold > 0
            ? +threshold
            : null;
    }

    refreshVisibleCategoryMetadata(updatedCategory) {
        if (updatedCategory && updatedCategory.id) {
            forEach(this.days || [], (day) => {
                const transactions = day && day.events ? day.events : [];

                forEach(transactions, (transaction) => {
                    const categoryId = transaction && transaction.category ? transaction.category.id : null;
                    if (`${categoryId}` === `${updatedCategory.id}`) {
                        transaction.category = angular.merge({}, transaction.category, updatedCategory);
                    }
                });

                if (day) {
                    day.filteredTransactions = this.filterDailyTransactions(day.events || []);
                }
            });

            return null;
        }

        const activeTeamId = this.selectedTeam && this.selectedTeam.id ? this.selectedTeam.id : this.teamId;
        if (!activeTeamId) {
            return this.reloadCalendar();
        }

        return this.categoryService.getAll(activeTeamId, true).then((categories) => {
            if (!categories || categories.length === 0) {
                return;
            }

            forEach(this.days || [], (day) => {
                const transactions = day && day.events ? day.events : [];

                forEach(transactions, (transaction) => {
                    const categoryId = transaction && transaction.category ? transaction.category.id : null;
                    if (!categoryId) {
                        return;
                    }

                    const updatedCategory = this.getCategoryById(categories, categoryId);
                    if (updatedCategory) {
                        transaction.category = angular.merge({}, transaction.category, updatedCategory);
                    }
                });

                if (day) {
                    day.filteredTransactions = this.filterDailyTransactions(day.events || []);
                }
            });
        }).catch(() => null);
    }

    getCategoryById(categories, categoryId) {
        return find(categories || [], (category) => category && category.id === categoryId) || null;
    }

    selectTeamIdAndCalendarIds(teamId, calendarIds, defaultTeamId, defaultCalendarIds) {
        let team = this.findTeamById(teamId);

        team = team
            ? team
            : defaultTeamId
                ? this.findTeamById(defaultTeamId) || this.getDefaultTeam()
                : this.getDefaultTeam();

        if (team) {
            calendarIds = calendarIds && calendarIds.length > 0
                ? calendarIds
                : defaultCalendarIds || [];

            let calendars = map(calendarIds, (calendarId) => this.getCalendarById(team.calendars || [], calendarId));
            return this.selectTeamAndCalendars(team, calendars);
        }

        return this.selectTeamAndCalendars();
    }

    selectTeamAndCalendars(team, calendars) {
        if (!team && this.teams.length > 0) {
            return this.selectTeamAndCalendars(this.teams[0], calendars);
        }

        if (team) {
            calendars = team
                ? filter(calendars || [], (calendar) => calendar && this.getCalendarById(team.calendars || [], calendar.id))
                : [];

            calendars = calendars.length > 0
                ? calendars
                : team.calendars && team.calendars.length > 0
                    ? [team.calendars[0]]
                    : [];
        } else {
            calendars = [];
        }

        if (!this.multiple) {
            calendars = calendars.slice(0, 1);
        }

        this.selectedTeam = team;
        this.selectedCalendarIds = calendars && calendars.length > 0
            ? map(calendars, (calendar) => calendar.id)
            : [];
        this.canManageEvents = this.getEditableCalendars(calendars).length > 0;
        this.updateCalendarHighlight();

        this.storageService.setUserItem('selected-team', this.selectedTeam ? this.selectedTeam.id : null);
        this.storageService.setUserItem('selected-calendars', this.selectedCalendarIds);

        return this.getSelectedTeamAndCalendars().then((data) => {
            let { selectedTeam, selectedCalendarIds } = data;

            return this.refreshCalendar(selectedCalendarIds, this.months, this.days).then(() => {
                this.teamService.getNoteMapping(selectedTeam.id).then((mapping) => {
                    this.suggestionService.initialize(selectedTeam.id, Object.keys(mapping));
                    this.autoCategoryService.initialize(selectedTeam.id, mapping);
                }).catch(() => null);

                return data;
            });
        });
    }

    updateCalendarHighlight() {
        const color = this.getCalendarHighlightColor();
        const palette = this.buildHighlightPalette(color);

        this.calendarHighlightPalette = palette;
        this.applyCalendarHighlight();
    }

    applyCalendarHighlight() {
        if (!this.calendarRoot || !this.calendarHighlightPalette) {
            return;
        }

        this.calendarRoot.style.setProperty('--calendar-highlight-color', this.calendarHighlightPalette.base);
        this.calendarRoot.style.setProperty('--calendar-highlight-color-hover', this.calendarHighlightPalette.hover);
        this.calendarRoot.style.setProperty('--calendar-highlight-color-shadow', this.calendarHighlightPalette.shadow);
        this.calendarRoot.style.setProperty('--calendar-highlight-color-fade', this.calendarHighlightPalette.fade);
        this.calendarRoot.style.setProperty('--calendar-highlight-color-pulse', this.calendarHighlightPalette.pulse);
    }

    getCalendarHighlightColor() {
        if (this.calendarHighlightPreference !== 'selected') {
            return '#1196ea';
        }

        const selectedCalendar = this.getPrimarySelectedCalendar();
        if (selectedCalendar && selectedCalendar.color) {
            return selectedCalendar.color;
        }

        return '#1196ea';
    }

    getPrimarySelectedCalendar() {
        if (!this.selectedTeam || !this.selectedTeam.calendars || !this.selectedCalendarIds || this.selectedCalendarIds.length === 0) {
            return null;
        }

        for (let index = 0; index < this.selectedCalendarIds.length; index += 1) {
            const calendar = this.getCalendarById(this.selectedTeam.calendars, this.selectedCalendarIds[index]);
            if (calendar) {
                return calendar;
            }
        }

        return null;
    }

    buildHighlightPalette(color) {
        const rgb = this.parseColorToRgb(color);

        if (!rgb) {
            return null;
        }

        const hover = this.adjustColor(rgb, -0.18);

        return {
            base: this.rgbToCss(rgb),
            hover: this.rgbToCss(hover),
            shadow: this.rgbToRgba(rgb, 0.5),
            fade: this.rgbToRgba(rgb, 0.5),
            pulse: this.rgbToRgba(rgb, 0.3)
        };
    }

    parseColorToRgb(color) {
        if (!color || typeof color !== 'string') {
            return null;
        }

        const trimmed = color.trim();

        if (trimmed.startsWith('#')) {
            return this.hexToRgb(trimmed);
        }

        return this.rgbStringToRgb(trimmed);
    }

    hexToRgb(hex) {
        let value = hex.replace('#', '');

        if (value.length === 3) {
            value = value.split('').map((char) => char + char).join('');
        }

        if (value.length !== 6) {
            return null;
        }

        const number = parseInt(value, 16);
        return {
            r: number >> 16 & 255,
            g: number >> 8 & 255,
            b: number & 255
        };
    }

    rgbStringToRgb(value) {
        const match = value.match(/^rgba?\(([^)]+)\)$/i);

        if (!match) {
            return null;
        }

        const parts = match[1].split(',').map((part) => part.trim());

        if (parts.length < 3) {
            return null;
        }

        const red = parseFloat(parts[0]);
        const green = parseFloat(parts[1]);
        const blue = parseFloat(parts[2]);

        if ([red, green, blue].some((component) => Number.isNaN(component))) {
            return null;
        }

        return {
            r: Math.max(0, Math.min(255, red)),
            g: Math.max(0, Math.min(255, green)),
            b: Math.max(0, Math.min(255, blue))
        };
    }

    adjustColor(color, amount) {
        const factor = 1 + amount;

        return {
            r: Math.max(0, Math.min(255, Math.round(color.r * factor))),
            g: Math.max(0, Math.min(255, Math.round(color.g * factor))),
            b: Math.max(0, Math.min(255, Math.round(color.b * factor)))
        };
    }

    rgbToCss(color) {
        return `rgb(${color.r}, ${color.g}, ${color.b})`;
    }

    rgbToRgba(color, alpha) {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    }

    selectCalendar(team, calendar) {
        return this.getSelectedTeamAndCalendars().then(({ selectedCalendarIds }) => {
            let filteredCalendarIds = filter(selectedCalendarIds, (id) => id !== calendar.id);

            if (filteredCalendarIds.length === 0 || filteredCalendarIds.length === selectedCalendarIds.length) {
                filteredCalendarIds.unshift(calendar.id);
            }

            return this.selectTeamIdAndCalendarIds(team.id, uniqBy(filteredCalendarIds));
        });
    }

    selectTeam(team) {
        return this.selectTeamIdAndCalendarIds(team.id, []);
    }

    getSelectedTeamAndCalendars() {
        return this.$q((resolve, reject) => {
            if (this.selectedTeam) {
                return resolve({
                    selectedTeam: this.selectedTeam,
                    selectedCalendarIds: this.selectedCalendarIds || []
                });
            }

            return reject(`No team is selected.`);
        });
    }

    $onDestroy() {
        if (this.dailyRefreshId) {
            this.dailyRefreshId();
        }

        if (this.pollRefreshId) {
            this.pollRefreshId();
        }

        this.suggestionService.reset();
        this.autoCategoryService.reset();
    }

    handleDefaultCalendarNotification(plan, teams) {
        let planExpired = plan.expired && !plan.free;
        let hasDefaultCalendar = some(teams, (team) => some(team.calendars, (calendar) => calendar.default));

        if (planExpired && !hasDefaultCalendar) {
            this.notificationService.show({
                component: 'dbARenewSubscriptionNotification',
                id: 'renew-subscription',
                bindings: {
                    plan: teams[0].plan,
                    onSelect: this.selectDefaultCalendar.bind(this)
                }
            }, true, 0);
        }
    }

    selectDefaultCalendar() {
        return this.loadTeams().then((teams) => {
            let calendars = reduce(teams, (calendars, team) => calendars.concat(team.calendars), []);

            let promise = calendars.length > 1
                ? this.modalService.open({
                    component: 'dbACalendarSelectDefaultModal',
                    bindings: {
                        teams: filter(teams, (team) => team.calendars && team.calendars.length > 0)
                    }
                })
                : this.$q.resolve(calendars[0]);

            return promise.then((calendar) => {
                return this.calendarService.markAsDefault(calendar).then(() => {
                    this.notificationService.show({
                        component: 'dbATextNotification',
                        id: 'default-calendar',
                        bindings: {
                            title: 'All good!',
                            message: `${calendar.name} has been marked as your default calendar.`
                        }
                    });

                    return this.loadTeams().then(() => {
                        return this.getSelectedTeamAndCalendars().then(({ selectedTeam, selectedCalendarIds }) => {
                            return this.selectTeamIdAndCalendarIds(selectedTeam.id, selectedCalendarIds);
                        });
                    });
                });
            });
        }).catch(() => null);
    }

    createMonths(timestamp) {
        timestamp = moment(timestamp).startOf('year').valueOf();

        let months = [];

        for (let index = 0; index < 12; ++index) {
            let date = moment(timestamp).add(index, 'month');

            months.push({
                timestamp: date.valueOf(),
                name: date.format('MMMM'),
                change: 0,
                balance: 0
            });
        }

        return months;
    }

    createWeekdays() {
        return this.timeUtil.getWeekdays(this.startOfWeek).map((day) => {
            return {
                name: moment(day.timestamp).format('dddd'),
                weekend: day.weekend
            };
        });
    }

    createDays(timestamp, days) {
        days = days || [];

        return this.timeUtil.getDaysForMonth(timestamp, this.startOfWeek).map((day, index) => {
            return angular.merge(days[index] || {}, {
                timestamp: day.timestamp,
                date: moment(day.timestamp).date(),
                another: day.previous || day.next,
                today: day.today
            });
        });
    }

    selectMonth(timestamp) {
        let selected = moment(this.timestamp);
        let target = moment(timestamp);

        if (target.year() !== selected.year()) {
            return this.refreshMonthlyBalances(timestamp);
        }

        return this.refreshDailyBalances(timestamp);
    }

    refreshDailyBalances(timestamp) {
        return this.getSelectedTeamAndCalendars().then(({ selectedCalendarIds }) => {
            timestamp = moment(timestamp).startOf('month').valueOf();

            let days = this.createDays(timestamp);

            return this.loadDailyBalances(selectedCalendarIds, days).catch((result) => {
                this.notificationService.showError(getErrorMessage(result, `We were not able to load your transactions for this year. Please try again later.`));
            }).finally(() => {
                this.days = days;
                this.timestamp = timestamp;
            });
        });
    }

    refreshMonthlyBalances(timestamp) {
        return this.getSelectedTeamAndCalendars().then((data) => {
            let { selectedCalendarIds } = data;
            let months = this.createMonths(timestamp);

            return this.$q.all([
                this.loadMonthlyBalances(selectedCalendarIds, months),
                this.refreshDailyBalances(timestamp)
            ]).then(() => data).catch((result) => {
                this.notificationService.showError(getErrorMessage(result, `We were not able to load your balances for this month. Please try again later.`));
            }).finally(() => this.months = months);
        }).catch(() => null);
    }

    selectSpecificMonth(timestamp) {
        analytics.trackCalendarSpecificMonthSelect();

        return this.selectMonth(timestamp);
    }

    selectPreviousMonth() {
        analytics.trackCalendarPreviousMonthSelect();

        return this.selectMonth(moment(this.timestamp).subtract(1, 'month').startOf('month'));
    }

    selectNextMonth() {
        analytics.trackCalendarNextMonthSelect();

        return this.selectMonth(moment(this.timestamp).add(1, 'month').startOf('month'));
    }

    selectToday() {
        analytics.trackCalendarTodaySelect();

        return this.selectMonth(moment().startOf('month').valueOf());
    }

    refreshCalendar(calendarIds) {
        return this.$q.all([
            this.loadMonthlyBalances(calendarIds, this.months),
            this.loadDailyBalances(calendarIds, this.days)
        ]).catch((result) => {
            this.notificationService.showError(getErrorMessage(result, `We were not able to refresh your calendar. You might not have access to this calendar anymore or somebody else logged in in your browser. Please reload the page.`));
        });
    }

    loadMonthlyBalances(calendarIds, months) {
        ++this.loadingMonthlyBalances;

        return this.balanceService.getMonthlyBalances(
            calendarIds,
            moment(months[0].timestamp).valueOf(),
            moment(months[months.length - 1].timestamp).endOf('month').valueOf()
        ).then((monthlyBalances) => {
            monthlyBalances.forEach((monthlyBalance) => {
                let month = find(months, (month) => month.timestamp === monthlyBalance.timestamp);
                if (month) {
                    month.change = monthlyBalance.revenue;
                    month.balance = monthlyBalance.balance;
                }
            });
        }).finally(() => --this.loadingMonthlyBalances);
    }

    loadDailyBalances(calendarIds, days) {
        ++this.loadingDailyBalances;

        return this.balanceService.getDailyBalances(
            calendarIds,
            days[0].timestamp,
            moment(days[days.length - 1].timestamp).endOf('day').valueOf()
        ).then((data) => {
            const dailyBalances = data.dailyBalances || [];
            const events = filter(data.transactions || [], (event) => !event.autoForecasted);

            dailyBalances.forEach((dailyBalance) => {
                let day = find(days, (day) => day.timestamp === dailyBalance.timestamp);
                if (day) {
                    day.change = dailyBalance.revenue;
                    day.balance = dailyBalance.balance;
                    day.events = filter(events, (event) => this.eventUtil.happenedOn(event, day.timestamp));
                    day.filteredTransactions = this.filterDailyTransactions(day.events);
                }
            });
        }).finally(() => --this.loadingDailyBalances);
    }

    getDefaultCalendar(team) {
        return team.calendars[0];
    }

    getDefaultTeam() {
        return find(this.teams || [], (team) => team && team.calendars.length > 0);
    }

    getTeamById(teamId) {
        return find(this.teams || [], (team) => team && team.id === teamId);
    }

    findCalendarById(calendarId) {
        return reduce(this.teams || [], (calendar, team) => calendar || this.getCalendarById(team.calendars, calendarId), null);
    }

    findTeamThatHasCalendarId(calendarId) {
        return find(this.teams || [], (team) => {
            return some(team.calendars || [], (calendar) => calendar.id === calendarId);
        });
    }

    getCalendarById(calendars, calendarId) {
        return find(calendars || [], (calendar) => calendar.id === calendarId);
    }

    loadTeams(promise) {
        ++this.loadingTeams;

        promise = promise || this.sessionService.reload();

        return promise.then((data) => {
            if (this.user && data.user && this.user.id !== data.user.id) {
                return this.$q.reject(`We were not able to refresh your calendar. You might not have access to this calendar anymore or somebody else logged in in your browser. Please reload the page.`);
            }

            this.user = data.user || {};
            this.plan = data.plan || {};
            this.plan.paid = !!this.plan.subscription && !!this.plan.subscription.type;

            this.teams = map(data.teams, this.decorateTeamWithPermissions.bind(this, this.user));

            if (this.selectedTeam) {
                this.selectedTeam = this.findTeamById(this.selectedTeam.id);
                if (!this.selectedTeam) {
                    return this.selectTeamIdAndCalendarIds().then(() => this.teams).catch(() => this.teams);
                }
            }

            this.updateCalendarHighlight();

            return this.teams;
        }).catch((result) => {
            this.notificationService.showError(getErrorMessage(result, `We were unable to load your teams and calendars. Please reload the page.`));

            return this.$q.reject(result);
        }).finally(() => --this.loadingTeams);
    }

    decorateTeamWithPermissions(user, team) {
        return angular.merge({}, team, {
            permissions: {
                edit: hasPermission(team.permissions, PERMISSION_TEAM_EDIT),
                delete: hasPermission(team.permissions, PERMISSION_TEAM_DELETE),
                addCalendar: hasPermission(team.permissions, PERMISSION_TEAM_ADD_CALENDAR),
                addMember: hasPermission(team.permissions, PERMISSION_TEAM_ADD_MEMBER),
                manageEvents: hasPermission(team.permissions, PERMISSION_TEAM_MANAGE_EVENTS),
                manageCategories: hasPermission(team.permissions, PERMISSION_TEAM_MANAGE_CATEGORIES)
            },
            calendars: map(team.calendars, this.decorateCalendarWithPermissions.bind(this)),
            owner: {
                isMe: !!team.owner && !!user && team.owner.id === user.id
            }
        });
    }

    decorateCalendarWithPermissions(calendar) {
        return angular.merge({}, calendar, {
            permissions: {
                edit: hasPermission(calendar.permissions, PERMISSION_CALENDAR_EDIT),
                delete: hasPermission(calendar.permissions, PERMISSION_CALENDAR_DELETE),
                manageEvents: hasPermission(calendar.permissions, PERMISSION_CALENDAR_MANAGE_EVENTS)
            }
        });
    }

    openCalendarList() {
        this.toggleNavigationBar(true);

        this.loadTeams().catch((result) => {
            this.notificationService.showError(getErrorMessage(result, `We were not able to refresh your teams and calendars. Please try again later.`));
            return this.$q.reject(result);
        });
    }

    toggleNavigationBar(visible) {
        this.navigationBarVisible = !!visible;
    }

    openCalendar(team, calendar) {
        return this.modalService.open({
            component: 'dbACalendarEditModal',
            bindings: {
                teamId: team.id,
                calendar: angular.extend({}, calendar)
            }
        });
    }

    editCalendar(team, calendar) {
        return this.getSelectedTeamAndCalendars().then((data) => {
            let { selectedTeam, selectedCalendarIds } = data;

            return this.openCalendar(team, calendar).then((updatedCalendar) => {
                if (updatedCalendar && updatedCalendar.id) {
                    this.applyCalendarUpdate(updatedCalendar);
                    this.updateCalendarHighlight();
                }

                return this.loadTeams().then(() => {
                    if (team && selectedTeam && selectedTeam.id === team.id) {
                        team = this.findTeamById(team.id);
                        return this.selectTeamIdAndCalendarIds(team ? team.id : null, selectedCalendarIds);
                    }

                    return data;
                });
            });
        }).catch(() => null);
    }

    addCalendar(team) {
        return this.openCalendar(team).then((calendar) => {
            return this.loadTeams().then(() => {
                return this.selectCalendar(team, calendar);
            });
        }).catch(() => null);
    }

    applyCalendarUpdate(updatedCalendar) {
        if (!updatedCalendar || !updatedCalendar.id) {
            return;
        }

        (this.teams || []).forEach((team) => {
            if (!team || !team.calendars) {
                return;
            }

            team.calendars = team.calendars.map((calendar) => {
                if (calendar && calendar.id === updatedCalendar.id) {
                    return angular.merge({}, calendar, updatedCalendar);
                }

                return calendar;
            });
        });

        if (this.selectedTeam && this.selectedTeam.calendars) {
            this.selectedTeam.calendars = this.selectedTeam.calendars.map((calendar) => {
                if (calendar && calendar.id === updatedCalendar.id) {
                    return angular.merge({}, calendar, updatedCalendar);
                }

                return calendar;
            });
        }
    }

    deleteCalendar(team, calendar) {
        return this.modalService.open({
            component: 'dbAConfirmModal',
            bindings: {
                title: 'Delete calendar',
                message: [
                    `Deleting a calendar cannot be undone. All the events added to this calendar will be erased.`,
                    `Are you still sure about it?`
                ]
            }
        }).then(() => {
            return this.calendarService.remove(calendar).catch((result) => {
                this.notificationService.showError(getErrorMessage(result, `We were not able to delete the calendar.`));
                return this.$q.reject(result);
            }).then(() => {
                return this.loadTeams().then(() => this.selectCalendar(team, calendar));
            });
        }).catch(() => null);
    }

    openTeam(team) {
        // TODO move the can manage collaborators permission to the backend and use that instead

        return this.modalService.open({
            component: 'dbATeamEditModal',
            bindings: {
                team,
                canEdit: team ? team.permissions.edit : true,
                canDelete: team ? team.permissions.delete : false,
                canManageCategories: team ? team.permissions.manageCategories : false,
                canManageCollaborators: team
                    ? this.isTeamOwnedBy(team, this.user) && this.canManageCollaboratorsByPlan(this.plan)
                    : this.canManageCollaboratorsByPlan(this.plan)
            }
        });
    }

    isTeamOwnedBy(team, user) {
        return team.owner.id === user.id;
    }

    canManageCollaboratorsByPlan(plan) {
        return getMaxNumberOfCollaborators(plan) > 0;
    }

    addTeam() {
        return this.openTeam().then((team) => {
            return this.loadTeams().then(() => this.selectCalendar(team, []));
        }).catch(() => null);
    }

    editTeam(team) {
        return this.openTeam(team).then((team) => {
            return this.loadTeams().then(() => {
                let exists = !!this.findTeamById(team.id);
                let selectedTeam = find(this.teams, (item) => item.id !== team.id);

                if (!exists) {
                    return this.selectCalendar(selectedTeam, []);
                }
            });
        }).catch(() => null);
    }

    findTeamById(id) {
        return find(this.teams, (team) => team.id === id);
    }

    getEditableCalendars(calendars) {
        return filter(calendars, (calendar) => calendar.permissions.manageEvents);
    }

    listEvents(day) {
        return this.getSelectedTeamAndCalendars().then(({ selectedCalendarIds }) => {
            return this.modalService.open({
                component: 'dbAEventListModal',
                bindings: {
                    timestamp: day.timestamp,
                    balance: () => day.balance,
                    change: () => day.change,
                    events: () => day.events,
                    canAddEvent: this.canManageEvents,
                    onTransactionCreate: (timestamp) => {
                        let day = this.getDayByTimestamp(this.days, timestamp);
                        return this.addEvent(day, 'list');
                    },
                    onTransactionEdit: (transaction) => {
                        return this.editEvent(transaction, 'list');
                    },
                    onTransactionConfirmChange: (transaction) => {
                        if (this.canEditTransaction(transaction)) {
                            let promise = transaction.templateId
                                ? this.eventService.updateRepeating(transaction, transaction.repeat.occurrence)
                                : this.eventService.update(transaction);

                            return promise.then((transaction) => {
                                analytics.trackTransactionConfirmChange(transaction.confirmed);

                                return this.refreshCalendar(selectedCalendarIds, this.months, this.days);
                            }).catch((result) => {
                                this.notificationService.showError(getErrorMessage(result, `We were not able to ${transaction.confirmed ? '' : 'un'}confirm the transaction. Please try again later.`));
                            });
                        }

                        return this.$q.reject(`We were unable to edit the transaction.`);
                    }
                }
            }).then(this.refreshCalendar.bind(this, selectedCalendarIds, this.months, this.days));
        }).catch(() => null);
    }

    canEditTransaction(transaction) {
        let calendar = this.findCalendarById(transaction.calendarId);
        return calendar && calendar.permissions.manageEvents;
    }

    getDayByTimestamp(days, timestamp) {
        let day = find(days, (day) => day.timestamp === timestamp);
        return angular.merge({}, day || {});
    }

    addEvent(day, from) {
        let now = moment();
        let date = moment(day.timestamp);

        date.hour(now.hour()).minute(now.minute()).second(now.second());

        return this.eventService.getInitial(date.valueOf()).then((event) => this.editEvent(event, from));
    }

    toggleMultiple(multiple) {
        this.multiple = multiple;
        this.storageService.setUserItem('multiple-calendars', this.multiple);

        return this.getSelectedTeamAndCalendars().then(({ selectedTeam, selectedCalendarIds }) => {
            return this.selectTeamIdAndCalendarIds(selectedTeam.id, selectedCalendarIds);
        });
    }

    editEvent(event, from) {
        return this.getSelectedTeamAndCalendars().then(({ selectedTeam, selectedCalendarIds }) => {
            let editableCalendars = this.getEditableCalendars(selectedTeam.calendars);
            let calendarIds = filter(selectedCalendarIds, (calendarId) => this.getCalendarById(editableCalendars, calendarId));

            let calendar = event.calendarId
                ? this.findCalendarById(event.calendarId)
                : this.findCalendarById(calendarIds[0]);

            if (!calendar) {
                return this.$q.reject(`We couldn't find the calendar this event is added to.`);
            }

            return this.modalService.open({
                component: 'dbAEventEditModal',
                bindings: {
                    teamId: selectedTeam.id,
                    calendarIds,
                    calendars: selectedTeam.calendars,
                    event: angular.merge({}, event),
                    startOfWeek: this.startOfWeek,
                    autoCategorization: this.autoCategorization,
                    from: from || 'calendar',
                    canEdit: calendar.permissions.manageEvents,
                    canDelete: calendar.permissions.manageEvents,
                    canManageCategories: selectedTeam.permissions.manageCategories
                }
            }).then(() => {
                return this.loadTeams().then(this.refreshCalendar.bind(this, selectedCalendarIds, this.months, this.days));
            });
        }).catch(() => null);
    }

    moveEvent(day, event) {
        let target = moment(day.timestamp);
        let source = moment(event.timestamp);

        source.year(target.year());
        source.month(target.month());
        source.date(target.date());

        event = angular.merge(event, {
            timestamp: source.valueOf()
        });

        let promise = event.templateId
            ? this.eventService.updateRepeating(event, event.repeat.occurrence)
            : this.eventService.update(event);

        return this.getSelectedTeamAndCalendars().then(({ selectedCalendarIds }) => {
            return promise.finally(() => {
                analytics.trackTransactionMove(event);
                return this.refreshCalendar(selectedCalendarIds, this.months, this.days);
            });
        }).catch(() => null);
    }

    updateEventLimit(limit) {
        this.limit = limit;
    }

    get canCaptureShortcuts() {
        return this.selectedCalendarIds && this.selectedCalendarIds.length > 0 && !this.navigationBarVisible && this.modalService.modals.length === 0;
    }

    initializeTransactionsByEmailNotification() {
        const saved = this.storageService.getUserItem(TRANSACTIONS_BY_EMAIL_NOTIFICATION_KEY);
        this.showTransactionsByEmailNotification = saved == null ? true : !!saved;
    }

    dismissTransactionsByEmailNotification() {
        this.showTransactionsByEmailNotification = false;
        this.storageService.setUserItem(TRANSACTIONS_BY_EMAIL_NOTIFICATION_KEY, false);
    }

    openTransactionsByEmailModal(team) {
        if (!team || !team.id) {
            return this.$q.resolve();
        }

        return this.modalService.open({
            component: 'dbAEmailIngestTokenModal',
            bindings: {
                teamId: team.id
            }
        }).catch(() => null);
    }

    openTransactionsByEmailFromShortcut() {
        this.dismissTransactionsByEmailNotification();
        return this.openTransactionsByEmailModal(this.selectedTeam);
    }

    get shouldShowTransactionsByEmailShortcut() {
        return !!this.showTransactionsByEmailNotification && !!this.selectedTeam;
    }

    searchTransactionsByTerms(terms) {
        this.searchTerms = terms;

        forEach(this.days, (day) => {
            day.filteredTransactions = this.filterDailyTransactions(day.events);
        });

        if (!this.hasSearchedBefore) {
            this.hasSearchedBefore = true;

            analytics.trackTransactionSearch();
        }
    }

    filterDailyTransactions(transactions) {
        return this.searchTerms && this.searchTerms.length > 0
            ? filter(transactions, (transaction) => transactionHasTerms(transaction, this.searchTerms))
            : transactions;
    }

    reorderCalendars(team, order) {
        return this.calendarService.reorder(team.id, map(order, (calendar) => calendar.id)).catch((result) => {
            this.notificationService.showError(getErrorMessage(result, `We were not able to reorder your calendars. Please try again later.`));
        }).finally(() => {
            return this.loadTeams().then(() => {
                return this.getSelectedTeamAndCalendars().then(({ selectedTeam, selectedCalendarIds }) => {
                    return this.selectTeamIdAndCalendarIds(selectedTeam.id, selectedCalendarIds);
                });
            });
        }).catch(() => null);
    }

    reorderTeams(order) {
        return this.teamService.reorder(map(order, (team) => team.id)).catch((result) => {
            this.notificationService.showError(getErrorMessage(result, `We were not able to reorder your teams. Please try again later.`));
        }).finally(() => {
            return this.loadTeams().then(() => {
                return this.getSelectedTeamAndCalendars().then(({ selectedTeam, selectedCalendarIds }) => {
                    return this.selectTeamIdAndCalendarIds(selectedTeam.id, selectedCalendarIds);
                });
            });
        }).catch(() => null);
    }
}

export default {
    selector: 'dbACalendar',
    template: template(),
    controller: CalendarController,
    bindings: {
        teamId: '<',
        calendarIds: '<'
    }
};



// WEBPACK FOOTER //
// ./src/application/scene/authorized/scene/calendar/calendar.cmpt.js